import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useParams } from 'react-router-dom'
import { useAccount } from '../api/get-account'
import { AiOutlineMail, AiOutlinePhone } from 'react-icons/ai'
import { Badge } from '@/components/ui/badge'
import PageLayout from '@/components/ui/page-layout'
import AccountDetailStores from '../components/AccountDetailStores'
import AccountUsers from '../components/AccountUsers'
import AccountDetailSubscription from '../components/AccountDetailSubscription'
import AccountBillingStatus from '../components/AccountBillingStatus'
import { ACCOUNT_TYPE } from '@/features/Customers/customers.constants'
import { getBillingRestrictionStageLabel, shouldShowBillingStatus } from '../billingRestriction'

// Assumes backup-tool/package.json only ever ships X.Y.Z releases (no -beta/-rc suffixes).
// If that ever changes, swap this for a real semver compare — parseInt strips the suffix
// and would silently mis-sort 2.0.10 vs 2.0.10-beta.
function compareSemver(a: string | null | undefined, b: string | null | undefined): number {
	if (!a || !b) return 0
	const aParts = a.split('.').map((p) => parseInt(p, 10) || 0)
	const bParts = b.split('.').map((p) => parseInt(p, 10) || 0)
	const len = Math.max(aParts.length, bParts.length)
	for (let i = 0; i < len; i++) {
		const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0)
		if (diff !== 0) return diff
	}
	return 0
}

export default function AccountDetails() {
	const { id } = useParams<{ id: string }>()
	const accountQuery = useAccount({ accountId: id || '' })
	const account = accountQuery.data

	if (accountQuery.isLoading || !account) {
		return <div>Loading...</div>
	}

	const billingRestrictionLabel = getBillingRestrictionStageLabel(
		account.billingRestriction,
	)

	return (
		<PageLayout
			title={account.name}
			subtitle={`Account ID: ${account.id}`}
			breadcrumbs={[
				{ label: 'Customers', href: '/customers' },
				{ label: account.name, href: `/accounts/${account.id}` },
			]}
		>
			<div className='grid grid-cols-1 lg:grid-cols-12 gap-4'>
				<Card className='lg:col-span-4'>
					<CardHeader>
						<CardTitle>Account Details</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='flex flex-wrap gap-2 mb-4'>
							{account.isDisabled ? (
								<Badge variant='destructive'>Disabled</Badge>
							) : (
								<Badge>Active</Badge>
							)}
							{billingRestrictionLabel && (
								<Badge variant='destructive'>
									Billing: {billingRestrictionLabel}
								</Badge>
							)}
							{account.backupToolInstalled ? (
								<Badge variant='outline'>Backup Tool Installed</Badge>
							) : (
								<Badge variant='destructive'>Backup Tool Not Installed</Badge>
							)}
							{account.backupToolInstanceVersions.length > 0 && (() => {
								const versions = account.backupToolInstanceVersions.map((version) => ({
									version,
									isOutdated: compareSemver(version, account.latestBackupToolVersion) < 0,
								}))
								const hasOutdated = versions.some((v) => v.isOutdated)
								return (
									<Badge variant='outline'>
										<span>
											Backup Tool Version:{' '}
											{versions.map(({ version, isOutdated }, idx) => (
												<span key={version}>
													<span className={isOutdated ? 'font-bold text-red-500' : undefined}>
														{version}
													</span>
													{idx < versions.length - 1 && ', '}
												</span>
											))}
											{hasOutdated && (
												<span className='font-normal text-foreground'>
													{' '}(latest {account.latestBackupToolVersion})
												</span>
											)}
										</span>
									</Badge>
								)
							})()}
						</div>

						<div className='grid gap-4 sm:grid-cols-2'>
							<div>
								<div className='text-xs uppercase tracking-wide text-muted-foreground'>Email</div>
								<div className='flex items-center gap-2 text-sm'>
									<AiOutlineMail />
									{account.email || '—'}
								</div>
							</div>
							<div>
								<div className='text-xs uppercase tracking-wide text-muted-foreground'>Phone</div>
								<div className='flex items-center gap-2 text-sm'>
									<AiOutlinePhone />
									{account.phone || '—'}
								</div>
							</div>
							<div>
								<div className='text-xs uppercase tracking-wide text-muted-foreground'>Account Type</div>
								<div className='text-sm'>{(ACCOUNT_TYPE as Record<number, string>)[account.type] ?? account.type}</div>
							</div>
							<div>
								<div className='text-xs uppercase tracking-wide text-muted-foreground'>Subscription ID</div>
								<div className='text-sm'>{account.subscriptionId || '—'}</div>
							</div>
							<div className='sm:col-span-2'>
								<div className='text-xs uppercase tracking-wide text-muted-foreground'>Address</div>
								<div className='text-sm'>
									{[account.streetAddress1, account.streetAddress2].filter(Boolean).join(' ') || '—'}
								</div>
								<div className='text-sm text-muted-foreground'>
									{[account.city, account.state].filter(Boolean).join(', ') || ''}
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
				<div className='col-span-8'>
					<AccountUsers accountId={id || ''} />
				</div>
				{shouldShowBillingStatus(account.billingDelinquency) && (
					<div className='lg:col-span-12'>
						<AccountBillingStatus
							accountId={account.id}
							billingDelinquency={account.billingDelinquency}
						/>
					</div>
				)}
				<div className='col-span-6'>
					<AccountDetailStores accountId={id || ''} />
				</div>
				<div className='col-span-6'>
					<AccountDetailSubscription accountId={id || ''} />
				</div>
			</div>

		</PageLayout>
	)
}
